import React, { useState } from 'react';
import './Projects.css';
import { FaChartBar, FaBrain, FaServer, FaExternalLinkAlt, FaGithub } from 'react-icons/fa';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const projects = [
    {
      id: 1,
      title: 'Named Entity Extraction from PDF (UiPath + AI Center)',
      description: 'Built an automation workflow that reads unstructured PDFs, sends text to a NER model, and converts model output into structured Excel-ready records.',
      image: <FaBrain />,
      category: 'nlp',
      technologies: ['UiPath Studio', 'UiPath AI Center', 'NER', 'JSON', 'Excel Activities'],
      impact: 'Reduced manual document parsing by mapping entities like name, email, skills, education, and experience into organized tabular outputs.',
      metric: 'Unstructured PDF to structured Excel',
      link: '#',
      github: '#'
    },
    {
      id: 2,
      title: 'Aura: AI Therapy Chatbot (Colab Deployment)',
      description: 'Fine-tuned and served a LLaMA-based therapy support assistant through Flask in Colab, with public access via Ngrok and optional text-to-speech responses.',
      image: <FaServer />,
      category: 'ml',
      technologies: ['LLaMA 3.2', 'Unsloth', 'PEFT', 'Flask', 'Ngrok', 'gTTS', 'Google Colab'],
      impact: 'Delivered an accessible chat API that supports both text and audio outputs while keeping the workflow practical for low-resource deployment.',
      metric: 'Text + audio via /chat endpoint',
      link: '#',
      github: '#'
    },
    {
      id: 3,
      title: 'GDP vs Cancer Mortality Correlation (India, 2010-2019)',
      description: 'Analyzed ten years of India cancer death-rate and GDP data using Karl-Pearson correlation and regression to validate an inverse-relationship hypothesis.',
      image: <FaChartBar />,
      category: 'analysis',
      technologies: ['Python', 'NumPy', 'Matplotlib', 'Karl-Pearson r', 'Regression'],
      impact: 'The analysis surfaced a strong positive relationship against the initial expectation, leading to deeper interpretation around reporting quality, demographics, and lifestyle shifts.',
      metric: 'Hypothesis tested on 10-year dataset',
      link: '#',
      github: '#'
    },
    {
      id: 4,
      title: 'Smart Inventory & Production Planner',
      description: 'Created a forecasting workflow for a D2C brand using ARIMA, EOQ, and safety stock logic to support production and replenishment decisions.',
      image: <FaChartBar />,
      category: 'ml',
      technologies: ['Python', 'ARIMA', 'EOQ', 'Forecasting'],
      impact: 'Helped teams anticipate demand and reduce stockout risk while avoiding unnecessary inventory pressure.',
      metric: 'Demand-aware planning workflow',
      link: '#',
      github: '#'
    },
    {
      id: 5,
      title: 'Speech-to-Speech Accessibility Pipeline',
      description: 'Developing an end-to-end STS pipeline that connects STT, LLM reasoning, and TTS to make educational interactions easier for visually impaired learners.',
      image: <FaBrain />,
      category: 'ml',
      technologies: ['Python', 'LangGraph', 'Open-Source LLMs', 'Linux', 'GCP VM', 'TTS/STT'],
      impact: 'Focused on accessibility impact by enabling more natural voice-based learning experiences for blind students.',
      metric: 'Accessibility-first AI system',
      link: '#',
      github: '#'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'ml', name: 'Machine Learning' },
    { id: 'analysis', name: 'Data Analysis' },
    { id: 'nlp', name: 'NLP' }
  ];

  const filteredProjects = activeFilter === 'all' ? projects : projects.filter((project) => project.category === activeFilter);

  return (
    <section id="projects" className="projects section">
      <div className="container">
        <h2 className="section-title">Featured Projects</h2>
        <p className="section-subtitle">
          Projects where I combined data, automation, and AI to solve practical problems.
        </p>

        <div className="project-filters">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`filter-btn ${activeFilter === category.id ? 'active' : ''}`}
              onClick={() => setActiveFilter(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>

        <div className="projects-list">
          {filteredProjects.map((project, index) => (
            <article key={project.id} className="project-row">
              <div className="project-side">
                <span className="project-index">0{index + 1}</span>
                <span className="project-kind">{project.category}</span>
                <div className="project-mark">{project.image}</div>
                <p className="project-metric">{project.metric}</p>
              </div>

              <div className="project-main">
                <div className="project-topline">
                  <h3 className="project-title">{project.title}</h3>
                  <div className="project-links">
                    <a href={project.link} className="project-link" target="_blank" rel="noopener noreferrer">
                      <FaExternalLinkAlt /> Live
                    </a>
                    <a href={project.github} className="project-link" target="_blank" rel="noopener noreferrer">
                      <FaGithub /> Code
                    </a>
                  </div>
                </div>

                <p className="project-description">{project.description}</p>

                <div className="project-tags">
                  {project.technologies.map((tech) => (
                    <span key={tech} className="project-tag">{tech}</span>
                  ))}
                </div>

                <div className="project-impact">
                  <span className="impact-label">Impact</span>
                  <p>{project.impact}</p>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="projects-cta">
          <span className="projects-kicker">More in the pipeline</span>
          <p>Interested in collaborating on a dashboard, analytics system, or ML workflow? Let&apos;s talk.</p>
          <button
            className="btn"
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Start a Conversation
          </button>
        </div>
      </div>
    </section>
  );
};

export default Projects;
